import { Box, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import ReportButton from "../../components/button/ReportButton";
import WorkspaceReportPanel from "../../components/panel/WorkspaceReportPanel";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SaveButton from "../../components/button/SaveButton";
import { MainContainer } from "../../components/container/MainContainer";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";
import { useAuth } from "../../authentication/AuthProvider";
import ExportProjectPPTButton from "../../components/export/ExportProjectPPTButton";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import SegmentedTabs from "../../components/tabs/SegmentedTabs";
import { Project } from "../../db_access/project";
import { Process } from "../../db_access/process";
import Footprint from "../../environment-page-components/Footprint";
import LifeCycleAssessment from "../../environment-page-components/LifeCycleAssessment";
import LifeCycleInventory from "../../environment-page-components/LifeCycleInventory";
import ProjectInfo from "../../environment-page-components/ProjectInfo";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { useTheme } from "@mui/material";
import SankeyDiagram from "../../environment-page-components/SankeyDiagram";
import ScopeAndGoals from "../../environment-page-components/ScopeAndGoals";
import SupplierInfo from "../../environment-page-components/SupplierInfo";
import SystemBoundary from "../../environment-page-components/SystemBoundary";
import ProjectInsight from "./Workspace/ProjectInsight";
import EvidenceRepository from "../../organizational-page-components/EvidenceRepository";
import routing from "../../routing";
import "../../style/pages-style/Utilizer.css";
// Cached hooks
import { useProjectById } from "../../utilities/useProjectQueries";

function ProjectWorkspace() {
  const { t } = useTranslation();
  const { selectedProjectIds, refresh, setRefresh, setIsSaveVisible, setIsLoading, setIsSaveClicked, setLeftSidebarCollapse } = useContextProvider();
  const { isAIInsightEnabled } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const navigate = useNavigate();
  const pageType = "utilizer";
  const LifeCycleAssessmentRef = useRef();

  const [activeTab, setActiveTab] = useState("Project Info");
  const project_info = { label: "Project Info", value: "Project Info" };
  const lca = { label: "Assessments", value: "Assessments" };
  const result = { label: "Results", value: "Results" };
  const evidence = { label: "Evidence Repository", value: "Evidence Repository" };
  const tabs = [project_info, lca, result, evidence];

  // Auto-collapse sidebar on entering workspace (particularly on Assessments tab) until user manually toggles it
  useEffect(() => {
    setLeftSidebarCollapse(true);
  }, [setLeftSidebarCollapse]);

  useEffect(() => {
    if (activeTab === lca.value) {
      setLeftSidebarCollapse(true);
    }
  }, [activeTab, lca.value, setLeftSidebarCollapse]);

  const projectId = selectedProjectIds.length === 1 ? selectedProjectIds[0] : null;

  // CACHED: Fetch project details
  const { data: selectedProject, isLoading: isProjectLoading } = useProjectById(projectId);

  // LOCAL STATE: Manage processes reactively (mirrors ProductWorkspace pattern)
  // This ensures the Results tab always gets fresh data after saving env factors
  const [processes, setProcesses] = useState([]);

  useEffect(() => {
    if (selectedProjectIds.length !== 1) {
      navigate(routing.sector.projects.dashboard);
    }
  }, [selectedProjectIds, navigate]);

  // Re-fetch processes whenever the selected project or refresh changes.
  // This mirrors ProductWorkspace's pattern and ensures the Results tab
  // (Footprint, SankeyDiagram, SystemBoundary) always reflects saved env factor changes.
  useEffect(() => {
    if (!selectedProject?.id) {
      setProcesses([]);
      return;
    }
    Process.get_by_project_ids([selectedProject.id])
      .then((res) => res.json())
      .then((data) => setProcesses(data || []))
      .catch((err) => console.error('[ProjectWorkspace] Error fetching processes:', err));
  }, [selectedProject, refresh]);

  // Sync loading state and reset flags
  useEffect(() => {
    if (selectedProject) {
      setIsSaveVisible(false);
      setIsLoading(false);
      setIsSaveClicked(false);
    } else if (isProjectLoading) {
      setIsLoading(true);
    }
  }, [selectedProject, isProjectLoading, setIsLoading, setIsSaveClicked, setIsSaveVisible]);

  return (
    <MainContainer>
      <Box sx={{ display: selectedProject ? "block" : "none" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: ORG_PALETTE.spacing.sectionGap }}>
          <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
            <Box sx={{ width: "100%", maxWidth: "800px" }}>
              <SegmentedTabs options={tabs} selected={activeTab} setSelected={setActiveTab} />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <ReportButton
              active={activeTab === "LCA Report"}
              onClick={() => setActiveTab("LCA Report")}
              label="LCA Report"
            />
          </Box>
        </Box>
        <Box sx={{ pt: 0 }}>
          <Box sx={{ 
            display: activeTab === project_info.value ? "flex" : "none", 
            flexDirection: "column", 
            gap: ORG_PALETTE.spacing.sectionGap,
            animation: "org-fadeInUp 0.4s ease" 
          }}>
            <ProjectInfo />
            <SupplierInfo type={Project.label} selectedRow={selectedProject} />
          </Box>
          <Box sx={{ 
            display: activeTab === lca.value ? "flex" : "none", 
            flexDirection: "column", 
            gap: ORG_PALETTE.spacing.sectionGap,
            animation: "org-fadeInUp 0.4s ease" 
          }}>
            <ScopeAndGoals pageType={"project"} selectedRow={selectedProject} />
            <LifeCycleInventory title={"Life Cycle Inventory"} selectedID={selectedProject?.id} isSupplier={false} isReuse={false} />
          </Box>
          <Box sx={{
            display: activeTab === result.value ? "flex" : "none",
            flexDirection: "column",
            gap: ORG_PALETTE.spacing.sectionGap,
            animation: "org-fadeInUp 0.4s ease"
          }}>
            <Footprint processData={processes} pageType={pageType} />
            <SankeyDiagram processData={processes} />
            <SystemBoundary processData={processes} />
            <LifeCycleAssessment ref={LifeCycleAssessmentRef} selectedRow={selectedProject} LCA_type={Project.label} />
            {isAIInsightEnabled && <ProjectInsight />}
          </Box>
          {activeTab === evidence.value && (
            <Box sx={{ animation: "org-fadeInUp 0.4s ease" }}>
              <EvidenceRepository entityId={selectedProject?.id} idColumnName="project_id" />
            </Box>
          )}
        </Box>
        {activeTab === "LCA Report" && (
          <Box sx={{ animation: "org-fadeInUp 0.4s ease" }}>
            <WorkspaceReportPanel
              entity={selectedProject}
              type="Project"
              processes={processes}
            />
          </Box>
        )}
      </Box>
      <SaveButton />
    </MainContainer>
  );
}

export default ProjectWorkspace;
